"use client";

import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import { IPackage } from "@/utilities/types/Package";
import {
  EStatusPayment,
  EXeroPaymentMethod,
  EXeroPaymentStatus,
  IItemPayment,
} from "@/utilities/types/Payment.ts";
import EyeIcon from "@/components/icons/eye.tsx";
import { ActionIcon, Tooltip } from "rizzui";
import { ChangeStatus } from "@/app/app-shared/package/payment-table/change-status.tsx";
import PaymentHistoryModal from "@/app/app-shared/package/payment-table/history-modal.tsx";
import { useState } from "react";

type PackageSortBy = "name" | "price" | "status" | "createdAt";

type Columns = {
  data: IItemPayment[];
  sortConfig?: SortConfig<PackageSortBy>;
  onHeaderCellClick?: (value: string) => void;
  handleReset?: () => void;
  refresh?: () => void;
  onUpdatePackage?: (id: string, data: Partial<IPackage>) => Promise<void>;
  changeStatusPayment: (
    id: string,
    type: string,
    reason?: string
  ) => Promise<void>;
};

const StatusPayment = Object.entries(EStatusPayment).reduce(
  (acc, [label, value]) => {
    acc[value] = label;
    return acc;
  },
  {} as Record<string, string>
);

const ButtonHistory = ({ xeroInvoiceID }: { xeroInvoiceID: string }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  return (
    <div>
      <Tooltip content={"View History"} color="invert">
        <ActionIcon size="sm" variant="outline" onClick={() => openModal()}>
          <EyeIcon className="h-4 w-4" />
        </ActionIcon>
      </Tooltip>
      {isModalOpen && (
        <PaymentHistoryModal
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          xeroInvoiceID={xeroInvoiceID}
        />
      )}
    </div>
  );
};

export const getColumns = ({
  sortConfig,
  onHeaderCellClick,
  changeStatusPayment,
}: Columns) => {
  return [
    {
      title: <HeaderCell title="Name" align="left" />,
      dataIndex: "name",
      key: "name",
      render: (_: string, row: IItemPayment) => {
        return (
          <span className="font-medium">{`${row?.user?.firstName} ${row?.user?.lastName}`}</span>
        );
      },
    },
    {
      title: <HeaderCell title="Email" align="left" />,
      dataIndex: "email",
      key: "email",
      render: (_: string, row: IItemPayment) => {
        return (
          <div className="flex flex-col gap-1">
            <span className="font-medium">{row?.user?.email}</span>
          </div>
        );
      },
    },
    {
      title: <HeaderCell title="Phone" align="left" />,
      dataIndex: "phone",
      key: "phone",
      render: (_: string, row: IItemPayment) => {
        return (
          <div className="flex flex-col gap-1">
            <span className="font-medium">{row?.user?.phone}</span>
          </div>
        );
      },
    },
    {
      title: (
        <HeaderCell
          title="Status lasted"
          align="left"
          sortable
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "status"
          }
        />
      ),
      dataIndex: "status",
      key: "status",
      onHeaderCell: () => onHeaderCellClick?.("status"),
      render: (status: EStatusPayment) => {
        return <span className="font-medium">{StatusPayment[status]}</span>;
      },
    },
    {
      title: <HeaderCell title="Method" align="left" />,
      dataIndex: "method",
      key: "method",
      render: (method: EXeroPaymentMethod) => {
        return (
          <span className="font-medium">{EXeroPaymentMethod[method]}</span>
        );
      },
    },
    {
      title: (
        <HeaderCell
          className=""
          align="left"
          title={<span className="whitespace-nowrap">Actions</span>}
        />
      ),
      dataIndex: "action",
      key: "action",
      align: "center",
      width: 120,
      render: (_: string, row: IItemPayment) => {
        return (
          <div className="flex items-center  gap-3 pe-4">
            <ButtonHistory xeroInvoiceID={row.id} />
            {row.status === EXeroPaymentStatus.INIT ||
              (row.status === EXeroPaymentStatus.PENDING && (
                <ChangeStatus
                  changeStatusPayment={changeStatusPayment}
                  item={row}
                />
              ))}
          </div>
        );
      },
    },
  ];
};
